import React, { useCallback, useEffect, useState } from 'react';
import { Extension, InputRule, type Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Mathematics, migrateMathStrings } from '@tiptap/extension-mathematics';
import 'katex/dist/katex.min.css';
import {
  Box,
  Paper,
  IconButton,
  Divider,
  Tooltip,
  ButtonGroup,
  Popover,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Image as ImageIcon,
  Link as LinkIcon,
  GridOn as TableIcon,
  Functions as MathIcon,
  Undo,
  Redo,
  FormatAlignLeft,
  FormatAlignCenter,
  Title,
  FormatColorText,
  EmojiEmotions as EmojiIcon,
  CloudUpload as UploadIcon,
  InsertLink as InsertLinkIcon,
} from '@mui/icons-material';

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  minHeight?: number;
}

type MathMode = 'inline' | 'block';

interface MathEditTarget {
  mode: MathMode;
  pos: number;
}

const LatexInputRules = Extension.create({
  name: 'latexInputRules',

  addInputRules() {
    return [
      new InputRule({
        find: /^\$\$([^$]+)\$\$$/,
        handler: ({ state, range, match }) => {
          const [, latex] = match;
          const blockMath = state.schema.nodes.blockMath;

          if (!blockMath || !latex.trim()) return;

          state.tr.replaceWith(range.from, range.to, blockMath.create({ latex: latex.trim() }));
        },
      }),
      new InputRule({
        find: /(^|[^\d$])(\$(?!\d+\$)([^$\n]+?)\$)(?!\d|\$)/,
        handler: ({ state, range, match }) => {
          const [, prefix, , latex] = match;
          const inlineMath = state.schema.nodes.inlineMath;

          if (!inlineMath || !latex.trim()) return;

          const start = range.from + prefix.length;
          state.tr.replaceWith(start, range.to, inlineMath.create({ latex: latex.trim() }));
        },
      }),
    ];
  },
});

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start typing...',
  readOnly = false,
  minHeight = 200,
}) => {
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [mathDialogOpen, setMathDialogOpen] = useState(false);
  const [mathLatex, setMathLatex] = useState('');
  const [mathMode, setMathMode] = useState<MathMode>('inline');
  const [mathEditTarget, setMathEditTarget] = useState<MathEditTarget | null>(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const migrateBlockLatexText = useCallback((editorInstance: Editor): boolean => {
    const blockMath = editorInstance.schema.nodes.blockMath;

    if (!blockMath) return false;

    const replacements: Array<{ pos: number; node: ProseMirrorNode; latex: string }> = [];

    editorInstance.state.doc.descendants((node, pos) => {
      if (node.type.name !== 'paragraph' || node.childCount !== 1) return;

      const text = node.firstChild?.text?.trim();
      const match = text?.match(/^\$\$([\s\S]+)\$\$$/);

      if (match?.[1]?.trim()) {
        replacements.push({ pos, node, latex: match[1].trim() });
      }
    });

    if (replacements.length === 0) return false;

    const transaction = editorInstance.state.tr;
    replacements.reverse().forEach(({ pos, node, latex }) => {
      transaction.replaceWith(pos, pos + node.nodeSize, blockMath.create({ latex }));
    });
    transaction.setMeta('addToHistory', false);
    editorInstance.view.dispatch(transaction);

    return true;
  }, []);

  const migrateLatexText = useCallback(
    (editorInstance: Editor) => {
      if (editorInstance.getText().includes('$')) {
        migrateBlockLatexText(editorInstance);
        migrateMathStrings(editorInstance);
      }
    },
    [migrateBlockLatexText],
  );

  const handleMathNodeClick = (mode: MathMode, node: ProseMirrorNode, pos: number) => {
    setMathMode(mode);
    setMathLatex(node.attrs?.latex || '');
    setMathEditTarget({ mode, pos });
    setMathDialogOpen(true);
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Image.configure({
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({
        multicolor: true,
      }),
      LatexInputRules,
      Mathematics.configure({
        katexOptions: {
          throwOnError: false,
        },
        inlineOptions: {
          onClick: readOnly ? undefined : (node, pos) => handleMathNodeClick('inline', node, pos),
        },
        blockOptions: {
          onClick: readOnly ? undefined : (node, pos) => handleMathNodeClick('block', node, pos),
        },
      }),
    ],
    content,
    editable: !readOnly,
    onCreate: ({ editor }) => {
      const beforeMigration = editor.getHTML();
      migrateLatexText(editor);
      const afterMigration = editor.getHTML();

      if (!readOnly && afterMigration !== beforeMigration) {
        onChange(afterMigration);
      }
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;

    if (content !== editor.getHTML()) {
      editor.commands.setContent(content || '', { emitUpdate: false });
      const beforeMigration = editor.getHTML();
      migrateLatexText(editor);
      const afterMigration = editor.getHTML();

      if (!readOnly && afterMigration !== beforeMigration) {
        onChange(afterMigration);
      }
    }
  }, [content, editor, migrateLatexText, onChange, readOnly]);

  if (!editor) {
    return null;
  }

  const handleColorClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    editor?.chain().focus().setColor(color).run();
    handleColorClose();
  };

  const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji: string) => {
    editor?.chain().focus().insertContent(emoji).run();
    handleEmojiClose();
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }

    // Check file size (limit to 2MB for performance)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image size should be less than 2MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      editor?.chain().focus().setImage({ src: base64 }).run();
      setImageDialogOpen(false);
      setUploadError('');
    };
    reader.readAsDataURL(file);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageDialogOpen = () => {
    setImageUrl('');
    setUploadError('');
    setImageDialogOpen(true);
  };

  const handleImageDialogClose = () => {
    setImageDialogOpen(false);
    setImageUrl('');
    setUploadError('');
  };

  const handleImageUrlSubmit = () => {
    if (imageUrl.trim()) {
      editor?.chain().focus().setImage({ src: imageUrl.trim() }).run();
      handleImageDialogClose();
    }
  };

  const handleLinkDialogOpen = () => {
    const { from, to } = editor?.state.selection || {};
    const selectedText = editor?.state.doc.textBetween(from || 0, to || 0, ' ');
    setLinkText(selectedText || '');
    setLinkUrl('');
    setLinkDialogOpen(true);
  };

  const handleLinkDialogClose = () => {
    setLinkDialogOpen(false);
    setLinkUrl('');
    setLinkText('');
  };

  const handleLinkSubmit = () => {
    if (linkUrl.trim()) {
      if (linkText.trim()) {
        // Insert new link with text
        editor
          ?.chain()
          .focus()
          .insertContent(`<a href="${linkUrl.trim()}">${linkText.trim()}</a>`)
          .run();
      } else {
        // Set link on selected text
        editor?.chain().focus().setLink({ href: linkUrl.trim() }).run();
      }
      handleLinkDialogClose();
    }
  };

  const handleMathDialogOpen = () => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    setMathMode('inline');
    setMathLatex(selectedText.replace(/^\$+|\$+$/g, '').trim());
    setMathEditTarget(null);
    setMathDialogOpen(true);
  };

  const handleMathDialogClose = () => {
    setMathDialogOpen(false);
    setMathLatex('');
    setMathMode('inline');
    setMathEditTarget(null);
  };

  const handleMathSubmit = () => {
    const latex = mathLatex.trim();
    if (!latex) return;

    if (mathEditTarget) {
      if (mathEditTarget.mode === 'block') {
        editor.chain().focus().updateBlockMath({ latex, pos: mathEditTarget.pos }).run();
      } else {
        editor.chain().focus().updateInlineMath({ latex, pos: mathEditTarget.pos }).run();
      }
      handleMathDialogClose();
      return;
    }

    if (mathMode === 'block') {
      editor.chain().focus().insertBlockMath({ latex }).run();
    } else {
      editor.chain().focus().insertInlineMath({ latex }).run();
    }

    handleMathDialogClose();
  };

  const colorPickerOpen = Boolean(colorAnchorEl);
  const emojiPickerOpen = Boolean(emojiAnchorEl);

  const emojis = [
    {
      category: 'Smileys',
      items: ['😀', '😊', '😂', '🤣', '😍', '🥰', '😎', '🤔', '😮', '😢', '😭', '😡'],
    },
    { category: 'Gestures', items: ['👍', '👎', '👏', '🙌', '👋', '🤝', '✌️', '🤞', '💪', '🙏'] },
    { category: 'Hearts', items: ['❤️', '💕', '💖', '💗', '💙', '💚', '💛', '🧡', '💜', '🖤'] },
    { category: 'Symbols', items: ['⭐', '✨', '💫', '🔥', '💯', '✅', '❌', '⚠️', '💡', '🎯'] },
    { category: 'Education', items: ['📚', '📖', '📝', '✏️', '📊', '📈', '🎓', '🏆', '🎉', '🎨'] },
    { category: 'Nature', items: ['🌟', '🌈', '🌸', '🌺', '🌻', '🌼', '🌷', '🌹', '🍀', '🌿'] },
  ];

  const predefinedColors = [
    { name: 'Black', value: '#000000' },
    { name: 'Dark Gray', value: '#4A4A4A' },
    { name: 'Gray', value: '#9B9B9B' },
    { name: 'Red', value: '#E74C3C' },
    { name: 'Orange', value: '#E67E22' },
    { name: 'Yellow', value: '#F39C12' },
    { name: 'Green', value: '#27AE60' },
    { name: 'Blue', value: '#3498DB' },
    { name: 'Purple', value: '#9B59B6' },
    { name: 'Pink', value: '#E91E63' },
    { name: 'Teal', value: '#1ABC9C' },
    { name: 'Brown', value: '#795548' },
  ];

  const MenuBar = () => (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        gap: 0.25,
        p: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.default',
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          height: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.2)',
          borderRadius: '3px',
        },
      }}
    >
      {/* Headings - Compact */}
      <Tooltip title="H1">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          color={editor.isActive('heading', { level: 1 }) ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <Title fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Bold">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
          color={editor.isActive('bold') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatBold fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Italic">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          color={editor.isActive('italic') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatItalic fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Underline">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          color={editor.isActive('underline') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Color">
        <IconButton
          size="small"
          onClick={handleColorClick}
          color={editor?.isActive('textStyle') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatColorText fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tooltip title="Bullet List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          color={editor.isActive('bulletList') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatListBulleted fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Numbered List">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          color={editor.isActive('orderedList') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatListNumbered fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tooltip title="Align Left">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatAlignLeft fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Align Center">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <FormatAlignCenter fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tooltip title="Insert Image">
        <IconButton size="small" onClick={handleImageDialogOpen} sx={{ minWidth: '32px' }}>
          <ImageIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Insert Link">
        <IconButton
          size="small"
          onClick={handleLinkDialogOpen}
          color={editor.isActive('link') ? 'primary' : 'default'}
          sx={{ minWidth: '32px' }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Table">
        <IconButton
          size="small"
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
          sx={{ minWidth: '32px' }}
        >
          <TableIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Insert LaTeX">
        <IconButton size="small" onClick={handleMathDialogOpen} sx={{ minWidth: '32px' }}>
          <MathIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Emoji">
        <IconButton size="small" onClick={handleEmojiClick} sx={{ minWidth: '32px' }}>
          <EmojiIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
      <Tooltip title="Undo">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          sx={{ minWidth: '32px' }}
        >
          <Undo fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Redo">
        <IconButton
          size="small"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          sx={{ minWidth: '32px' }}
        >
          <Redo fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <>
      {/* Hidden file input for image upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleImageUpload}
      />

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        {!readOnly && <MenuBar />}
        <Box
          sx={{
            p: 2,
            minHeight,
            maxHeight: readOnly ? 'none' : 600,
            overflowY: 'auto',
            '& .ProseMirror': {
              outline: 'none',
              minHeight: readOnly ? 'auto' : minHeight - 32,
              '& p.is-editor-empty:first-of-type::before': {
                content: 'attr(data-placeholder)',
                float: 'left',
                color: 'text.disabled',
                pointerEvents: 'none',
                height: 0,
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '350px',
                objectFit: 'contain',
                display: 'block',
                margin: '1rem auto',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              },
            },
          }}
        >
          <EditorContent editor={editor} />
        </Box>
      </Paper>

      {/* Color Picker Popover */}
      <Popover
        open={colorPickerOpen}
        anchorEl={colorAnchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
          {predefinedColors.map((color) => (
            <Tooltip key={color.value} title={color.name}>
              <IconButton
                onClick={() => handleColorSelect(color.value)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: color.value,
                  border: '2px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    backgroundColor: color.value,
                    opacity: 0.8,
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Popover>

      {/* Emoji Picker Popover */}
      <Popover
        open={emojiPickerOpen}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 320, maxHeight: 400, overflowY: 'auto' }}>
          {emojis.map((category) => (
            <Box key={category.category} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                {category.category}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
                {category.items.map((emoji) => (
                  <IconButton
                    key={emoji}
                    onClick={() => handleEmojiSelect(emoji)}
                    sx={{
                      fontSize: '1.5rem',
                      width: 40,
                      height: 40,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {emoji}
                  </IconButton>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Image Insert Dialog */}
      <Dialog open={imageDialogOpen} onClose={handleImageDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Image</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {uploadError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {uploadError}
              </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose how you want to add an image:
            </Typography>

            {/* Upload from computer */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ py: 1.5 }}
              >
                Upload from Computer
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Maximum file size: 2MB
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                OR
              </Typography>
            </Divider>

            {/* Enter URL */}
            <Box>
              <TextField
                fullWidth
                label="Image URL"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleImageUrlSubmit();
                  }
                }}
                InputProps={{
                  startAdornment: <InsertLinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImageDialogClose}>Cancel</Button>
          <Button onClick={handleImageUrlSubmit} variant="contained" disabled={!imageUrl.trim()}>
            Insert URL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Insert Dialog */}
      <Dialog open={linkDialogOpen} onClose={handleLinkDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Insert Link</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Link Text"
              placeholder="Click here"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              helperText="Leave empty to use selected text"
            />
            <TextField
              fullWidth
              label="URL"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLinkSubmit();
                }
              }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLinkDialogClose}>Cancel</Button>
          <Button onClick={handleLinkSubmit} variant="contained" disabled={!linkUrl.trim()}>
            Insert Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* LaTeX Math Dialog */}
      <Dialog open={mathDialogOpen} onClose={handleMathDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{mathEditTarget ? 'Edit LaTeX' : 'Insert LaTeX'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!mathEditTarget && (
              <ButtonGroup size="small" variant="outlined" aria-label="LaTeX display mode">
                <Button
                  variant={mathMode === 'inline' ? 'contained' : 'outlined'}
                  onClick={() => setMathMode('inline')}
                >
                  Inline
                </Button>
                <Button
                  variant={mathMode === 'block' ? 'contained' : 'outlined'}
                  onClick={() => setMathMode('block')}
                >
                  Display
                </Button>
              </ButtonGroup>
            )}
            <TextField
              fullWidth
              multiline
              minRows={mathMode === 'block' ? 3 : 1}
              label="LaTeX"
              placeholder="x^2 + y^2 = z^2"
              value={mathLatex}
              onChange={(e) => setMathLatex(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  handleMathSubmit();
                }
              }}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMathDialogClose}>Cancel</Button>
          <Button onClick={handleMathSubmit} variant="contained" disabled={!mathLatex.trim()}>
            {mathEditTarget ? 'Update' : 'Insert'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
